import React, { useState, useEffect, useRef } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, X, LogOut, FileText, Heart, ChevronDown, LogIn } from "lucide-react";
import Logo from "./Logo";
import { useAuth } from "@/lib/AuthContext";

const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/domain-portfolio", label: "Portfolio" },
  { to: "/software-portfolio", label: "Software" },
  { to: "/about", label: "About" },
  { to: "/investors", label: "Investors" },
];

function ProfileMenu() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  if (!user) return null;
  const initials = (user.name || user.email).split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 pl-1 pr-3 py-1 border border-slate-200 hover:border-[#0066FF] rounded-full transition-colors"
        data-testid="profile-menu-trigger"
      >
        {user.picture ? (
          <img src={user.picture} alt="" className="h-8 w-8 rounded-full object-cover" />
        ) : (
          <span className="h-8 w-8 rounded-full bg-[#0066FF] text-white flex items-center justify-center text-xs font-semibold">
            {initials}
          </span>
        )}
        <span className="hidden xl:inline text-sm text-slate-700 font-medium max-w-[120px] truncate">
          {(user.name || user.email).split(" ")[0]}
        </span>
        <ChevronDown size={14} className="text-slate-500" />
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-2 w-64 bg-white border border-slate-200 shadow-[0_8px_32px_rgba(15,23,42,0.08)] py-2 z-50"
          data-testid="profile-menu"
        >
          <div className="px-4 py-3 border-b border-slate-100">
            <div className="font-display text-sm font-semibold text-slate-900 truncate">{user.name}</div>
            <div className="text-xs text-slate-500 truncate mt-0.5">{user.email}</div>
            {user.role === "admin" && (
              <span className="mt-2 inline-block text-[0.6rem] font-mono uppercase tracking-[0.15em] px-2 py-0.5 border border-[#C8961F] text-[#C8961F]">
                Admin
              </span>
            )}
          </div>
          <Link
            to="/my-submissions"
            className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 hover:text-[#0066FF] transition-colors"
            data-testid="profile-link-submissions"
            onClick={() => setOpen(false)}
          >
            <FileText size={14} /> My Submissions
          </Link>
          <Link
            to="/domain-portfolio"
            className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 hover:text-[#0066FF] transition-colors"
            data-testid="profile-link-favorites"
            onClick={() => setOpen(false)}
          >
            <Heart size={14} /> Favorite Domains
          </Link>
          {user.role === "admin" && (
            <Link
              to="/admin"
              className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 hover:text-[#0066FF] transition-colors border-t border-slate-100"
              data-testid="profile-link-admin"
              onClick={() => setOpen(false)}
            >
              Admin Panel
            </Link>
          )}
          <button
            onClick={() => { setOpen(false); logout(); }}
            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 hover:text-red-500 transition-colors border-t border-slate-100"
            data-testid="profile-logout-btn"
          >
            <LogOut size={14} /> Sign Out
          </button>
        </div>
      )}
    </div>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { user, loading, loginWithGoogle } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/90 backdrop-blur-xl border-b border-slate-200 shadow-[0_1px_8px_rgba(15,23,42,0.04)]"
          : "bg-white/70 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex items-center justify-between h-[88px] gap-4">
          <Link to="/" data-testid="navbar-logo-link" className="shrink-0">
            <Logo size="md" />
          </Link>

          <nav className="hidden lg:flex items-center gap-0.5 flex-1 justify-center max-w-2xl" data-testid="navbar-links">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `relative px-3 xl:px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
                    isActive ? "text-[#0066FF]" : "text-slate-700 hover:text-[#0066FF]"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {l.label}
                    {isActive && (
                      <span className="absolute -bottom-0.5 left-3 right-3 xl:left-4 xl:right-4 h-0.5 bg-[#0066FF]" />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3 shrink-0">
            {loading ? (
              <div className="h-9 w-9 bg-slate-100 rounded-full animate-pulse" />
            ) : user ? (
              <ProfileMenu />
            ) : (
              <button
                onClick={loginWithGoogle}
                className="btn-ghost text-sm font-medium border border-slate-200 hover:border-[#0066FF] py-2 px-4"
                data-testid="navbar-google-login-btn"
              >
                <LogIn size={14} /> Sign In
              </button>
            )}
            <Link
              to="/contact"
              data-testid="navbar-contact-cta"
              className="btn-primary text-sm py-2.5 px-5"
            >
              Contact Us
            </Link>
          </div>

          <button
            onClick={() => setOpen(!open)}
            className="lg:hidden p-2 text-slate-900 hover:text-[#0066FF] transition-colors"
            data-testid="navbar-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-slate-200" data-testid="navbar-mobile-menu">
          <div className="px-5 py-4 flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `px-4 py-3 text-sm font-medium border-l-2 transition-colors ${
                    isActive
                      ? "text-[#0066FF] border-[#0066FF] bg-[#E6F0FF]/50"
                      : "text-slate-700 border-transparent hover:text-[#0066FF] hover:border-slate-300"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="mt-3 flex flex-col gap-2">
              {user ? (
                <>
                  <Link to="/my-submissions" className="btn-secondary text-sm justify-center" data-testid="navbar-mobile-mysubs">
                    My Submissions
                  </Link>
                  <Link to="/contact" className="btn-primary text-sm justify-center" data-testid="navbar-mobile-contact-cta">
                    Contact Us
                  </Link>
                </>
              ) : (
                <>
                  <button
                    onClick={loginWithGoogle}
                    className="btn-secondary text-sm justify-center"
                    data-testid="navbar-mobile-google-login-btn"
                  >
                    <LogIn size={14} /> Sign In with Google
                  </button>
                  <Link to="/contact" className="btn-primary text-sm justify-center" data-testid="navbar-mobile-contact-cta">
                    Contact Us
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
